import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  adminRights,
  brokerRights,
  celticsRights,
  eeRights,
  brokerOrEmployerRights,
  loginUserRights,
  imCelticsRights,
  enrollmentRights,
  uwRights,
  zywaveUserRights,
} from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import ProductOutputsPage from 'pages/ProductOutputsPage';
import { ProposalGenerationFlow } from 'pages/proposal-generation';
import { AddEmployeesFlow } from 'pages/enrollment/add-employees';
import PostSubmitChecklistPage from 'pages/post-submit/PostSubmitChecklistPage';
import PostSubmitDocumentsPage from 'pages/post-submit/PostSubmitDocumentsPage';
import RFQProposalChecklistPage from 'pages/rfq/RFQProposalChecklistPage';
import ContributionFormPage from 'pages/enrollment/ContributionFormPage';
import { CloseDealPage } from 'pages/CloseDealPage';
import InitiateAddEmployeesPage from 'pages/rfq/initiate-rfq/InitiateAddEmployeesPage';
import EmployeeSelfRegisterLinkPage from 'pages/utility/EmployeeSelfRegisterLinkPage';
import MonitorAddEmployeesPage from 'pages/rfq/MonitorAddEmployeesPage';
import CensusInitiateAddEeUploadPage from 'pages/rfq/CensusInitiateAddEeUploadPage';
import CensusInitiateAddEeReviewPage from 'pages/rfq/CensusInitiateAddEeReviewPage';
import CensusMonitorAddEeUploadPage from 'pages/rfq/CensusMonitorAddEeUploadPage';
import CensusMonitorAddEeReviewPage from 'pages/rfq/CensusMonitorAddEeReviewPage';
import SubscriberTaskChecklistPage from 'pages/subscriber/SubscriberTaskChecklistPage';
import SubscriberProfilePage from 'pages/subscriber/SubscriberProfilePage';
import QuoteSubscriberProfilePage from 'pages/subscriber/QuoteSubscriberProfilePage';
import QuoteManageElectionsPage from 'pages/subscriber/QuoteManageElectionsPage';
import SubscriberDependentsPage from 'pages/subscriber/SubscriberDependentsPage';
import DownloadIMQsPage from 'pages/rfq/initiate-rfq/DownloadIMQsPage';
import AddEmployeeFormPage from 'pages/utility/AddEmployeeFormPage';
import ProposalOverrideRatesPage from 'pages/ProposalOverrideRatesPage';
import OverrideUwRatesPage from 'pages/OverrideUwRatesPage';
import OverrideQuotedRatesPage from 'pages/OverrideQuotedRatesPage';
import { ManageAddEmployeesFlow } from 'pages/enrollment/manage-add-employess';
import FullFormPage from 'pages/utility/FullFormPage';
import AlliedBrokerPage from 'pages/enrollment/broker/AlliedBrokerPage';
import PostSubmitDownloadProposalPage from 'pages/post-submit/PostSubmitDownloadProposalPage';
import DefaultBrokerPage from 'pages/enrollment/broker/DefaultBrokerPage';
import SunLifeBrokerPage from 'pages/enrollment/broker/SunlifeBrokerPage';
import {
  AdditionalOutputsPage,
  BiDashboardPage,
  BookOfBusinessPage,
  BrowsingPage,
  CensusEnrollmentPage as CensusImportPageDeprecated,
  ChecklistPage,
  CompanyInfoFormsPage,
  ComparePortfoliosPage,
  DashboardPage,
  EeClassesPage,
  ElectionsPage,
  EmailsDashboardPage,
  EmployeeProfilePage,
  EmployerFormsPage,
  EnrollmentPacketPage,
  Error404Page,
  FavoriteProposalsPage,
  FilesTrackingPage,
  GroupInfoPage,
  InsightsPage,
  ManageSubscriberPage,
  NotificationSettingsPage,
  ProductClassesPage,
  ProposalPage,
  RenewalFormPage,
  RenewalFormsPage,
  ReviewSubmitPage,
  ReviewSubscriberPage,
  SummaryPage,
  TriggerRenewalsPage,
  UploadExhibitsPage,
  UploadSyncFilesPage,
  WebFormPage,
  RFQProductTemplatePages,
  RFQResponsePages,
  InitiateRFQPages,
  RequiredDocumentsPage,
  CensusReviewPage as CensusReviewPageDeprecated,
  WelcomeKitPage,
  SubscriberIMQDocumentsPage,
} from '../pages';

export interface RouteConfig {
  /**
   * Page URL address.
   */
  path: string;
  /**
   * React page component that will be rendered when the route is matched.
   */
  component: React.ComponentType;
  /**
   * Role check function.
   * This function should take an array of user roles and return a boolean indicating whether the user has access to the route.
   */
  roleCheck?: (userRoles: SupportedUserRoles[]) => boolean;
  /**
   * A react-router-dom prop.
   */
  exact?: boolean;
}

/**
 *  DEVELOPER NOTE:
 *  ---------------
 *  We gradually migrate to the new routing system. This decision was made to standardize our routing and
 *  introduce enhanced features like breadcrumbs. The new routes are kept in a map instead of an array. This way
 *  they may be used as constants and are not scattered around the codebase.
 *
 *  Please note, that if the new page uses breadcrumbs, it will also have a new sidebar in it. It means that
 *  the whole flow where this page is used should be refactored into the new route structure. Technically,
 *  the old and the new pages may be mixed together, just that it may confuse our users and thus is discouraged.
 *  ---------------
 */

// Routes are sorted in the order of app flow direction.
/**
 * @deprecated Use routesConfigV2 for new flows.
 */
const generalRoutes: RouteConfig[] = [
  { path: '/dashboard', component: DashboardPage, roleCheck: brokerRights },
  { path: '/group/:groupId', component: GroupInfoPage, roleCheck: brokerRights },
  { path: '/browsing', component: BrowsingPage, roleCheck: brokerOrEmployerRights },
  { path: '/product-compare', component: ComparePortfoliosPage, roleCheck: loginUserRights },
  { path: '/proposal-v2/:periodId', component: ProposalPage, roleCheck: brokerOrEmployerRights },
  {
    path: '/generate-proposal',
    component: ProposalGenerationFlow,
    roleCheck: brokerOrEmployerRights,
    exact: false,
  },
  {
    path: '/proposal-override-rates/:periodId',
    component: ProposalOverrideRatesPage,
    roleCheck: uwRights,
  },
  {
    path: '/override-quoted-rates/:groupId/:periodId/:cartId/:portfolioId/:productLineId/:bcIndex',
    component: OverrideQuotedRatesPage,
    roleCheck: uwRights,
  },
  { path: '/agent-of-record/:groupId/:periodId', component: DefaultBrokerPage, roleCheck: brokerRights },
  { path: '/allied-agent-of-record/:groupId/:periodId', component: AlliedBrokerPage, roleCheck: brokerRights },
  { path: '/sunlife-agent-of-record/:groupId/:periodId', component: SunLifeBrokerPage, roleCheck: brokerRights },
  { path: '/company-info/:groupId/:periodId', component: CompanyInfoFormsPage, roleCheck: enrollmentRights },
  { path: '/contribution-form/:groupId/:periodId', component: ContributionFormPage, roleCheck: enrollmentRights },
  { path: '/forms/:periodId', component: EmployerFormsPage, roleCheck: enrollmentRights },
  { path: '/web-form/:periodId/:formInstanceId', component: WebFormPage, roleCheck: enrollmentRights },
  { path: '/renewal-forms/:periodId', component: RenewalFormsPage, roleCheck: enrollmentRights },
  { path: '/renewal-form/:periodId/:formInstanceId', component: RenewalFormPage, roleCheck: enrollmentRights },
  { path: '/required-documents/:periodId', component: RequiredDocumentsPage, roleCheck: enrollmentRights },
  // @deprecated step, remove after full migration to the add-employees page.
  { path: '/census/:groupId/:periodId', component: CensusImportPageDeprecated, roleCheck: enrollmentRights },
  // @deprecated step, remove after full migration to the add-employees page.
  { path: '/census-review/:groupId/:periodId', component: CensusReviewPageDeprecated, roleCheck: enrollmentRights },
  {
    path: '/add-employees',
    component: AddEmployeesFlow,
    roleCheck: enrollmentRights,
    exact: false,
  },
  {
    path: '/manage-add-employees',
    component: ManageAddEmployeesFlow,
    roleCheck: enrollmentRights,
    exact: false,
  },
  { path: '/employee-elections/:periodId', component: ElectionsPage, roleCheck: enrollmentRights },
  { path: '/ee-classes/:periodId', component: EeClassesPage, roleCheck: enrollmentRights },
  { path: '/product-classes/:periodId', component: ProductClassesPage, roleCheck: enrollmentRights },
  { path: '/review-submit/:periodId', component: ReviewSubmitPage, roleCheck: enrollmentRights },
  {
    path: '/override-uw-rates/:periodId/:cartId/:portfolioId/:productLineId',
    component: OverrideUwRatesPage,
    roleCheck: uwRights,
  },
  { path: '/post-submit-documents/:periodId', component: PostSubmitDocumentsPage, roleCheck: enrollmentRights },
  {
    path: '/download-proposal/:periodId/:groupId/:cartId',
    component: PostSubmitDownloadProposalPage,
    roleCheck: enrollmentRights,
  },
  { path: '/enrollment-packet/:periodId', component: EnrollmentPacketPage, roleCheck: enrollmentRights },
  { path: '/additional-outputs/:periodId', component: AdditionalOutputsPage, roleCheck: enrollmentRights },
  { path: '/docx-based-outputs/:periodId', component: WelcomeKitPage, roleCheck: enrollmentRights },
  { path: '/summary/:periodId', component: SummaryPage, roleCheck: enrollmentRights },
  { path: '/notification-settings', component: NotificationSettingsPage, roleCheck: brokerRights },
  // TO_DO: Create a new, better page for account settings.
  // { path: '/account-settings', component: AccountSettingsPage, roleCheck: brokerRights },
  { path: '/product-outputs/:periodId', component: ProductOutputsPage, roleCheck: enrollmentRights },
  { path: '/close-deal/:periodId', component: CloseDealPage, roleCheck: enrollmentRights },
  { path: '/error-404', component: Error404Page },
];

/**
 * @deprecated Use routesConfigV2 for new flows.
 */
const checklistsRoutes: RouteConfig[] = [
  { path: '/checklist/:periodId', component: ChecklistPage, roleCheck: enrollmentRights },
  { path: '/rfq-checklist/:periodId', component: RFQProposalChecklistPage, roleCheck: brokerOrEmployerRights },
  {
    path: '/product-template-checklist/:templateId',
    component: RFQProductTemplatePages.RFQProductTemplateChecklistPage,
    roleCheck: celticsRights,
  },
  { path: '/post-submit-checklist/:periodId', component: PostSubmitChecklistPage, roleCheck: enrollmentRights },
  { path: '/subscriber-task-checklist/:groupId', component: SubscriberTaskChecklistPage, roleCheck: eeRights },
];

/**
 * @deprecated Use routesConfigV2 for new flows.
 */
const toolsRoutes: RouteConfig[] = [
  { path: '/bi-dashboard', component: BiDashboardPage, roleCheck: celticsRights },
  { path: '/favorite-proposals', component: FavoriteProposalsPage, roleCheck: brokerRights },
  { path: '/book-of-business', component: BookOfBusinessPage, roleCheck: adminRights },
  { path: '/insights', component: InsightsPage, roleCheck: adminRights },
];

/**
 * @deprecated Use routesConfigV2 for new flows.
 */
const baToolsRoutes: RouteConfig[] = [
  { path: '/trigger-renewals', component: TriggerRenewalsPage, roleCheck: celticsRights },
  { path: '/upload-exhibits', component: UploadExhibitsPage, roleCheck: imCelticsRights },
  { path: '/upload-sync-files', component: UploadSyncFilesPage, roleCheck: imCelticsRights },
  { path: '/files-tracking', component: FilesTrackingPage, roleCheck: imCelticsRights },
  { path: '/emails-dashboard', component: EmailsDashboardPage, roleCheck: imCelticsRights },
];

/**
 * @deprecated Use routesConfigV2 for new flows.
 */
const subscriberRoutes: RouteConfig[] = [
  { path: '/employee', component: EmployeeProfilePage, roleCheck: eeRights },
  { path: '/manage-subscriber/:periodId', component: ManageSubscriberPage, roleCheck: loginUserRights },
  { path: '/review-subscriber/:periodId', component: ReviewSubscriberPage, roleCheck: loginUserRights },
  { path: '/subscriber-profile/:groupId', component: SubscriberProfilePage, roleCheck: eeRights },
  { path: '/quote-subscriber-profile/:groupId', component: QuoteSubscriberProfilePage, roleCheck: eeRights },
  { path: '/quote-manage-elections/:groupId', component: QuoteManageElectionsPage, roleCheck: eeRights },
  { path: '/subscriber-manage-dependents/:groupId', component: SubscriberDependentsPage, roleCheck: eeRights },
  { path: '/subscriber-imq-documents/:groupId', component: SubscriberIMQDocumentsPage, roleCheck: loginUserRights },
];

/**
 * @deprecated Use routesConfigV2 for new flows.
 */
const utilityRoutes: RouteConfig[] = [
  // TO_DO: After level-funded resurrection, this one should probably be moved to the internal AddEmployees page router.
  // As it is uniquely enrollment-specific.
  { path: '/full-employee-form/:groupId/:periodId', component: FullFormPage, roleCheck: brokerOrEmployerRights },
  {
    path: '/add-employee-form/:groupId',
    component: AddEmployeeFormPage,
    roleCheck: (userRoles: SupportedUserRoles[]): boolean =>
      brokerOrEmployerRights(userRoles) || zywaveUserRights(userRoles),
  },
  {
    path: '/employee-self-register-link/:periodId',
    component: EmployeeSelfRegisterLinkPage,
    roleCheck: (userRoles: SupportedUserRoles[]): boolean =>
      brokerOrEmployerRights(userRoles) || zywaveUserRights(userRoles),
  },
];

/**
 * @deprecated Use routesConfigV2 for new flows.
 */
const rfqTemplateRoutes: RouteConfig[] = [
  {
    path: '/product-templates',
    component: RFQProductTemplatePages.RFQProductTemplatesPage,
    roleCheck: celticsRights,
  },
  {
    path: '/shelf-product-templates',
    component: RFQProductTemplatePages.ShelfRFQProductTemplatesPage,
    roleCheck: celticsRights,
  },
  {
    path: '/initial-seed-templates',
    component: RFQProductTemplatePages.InitialSeedProductTemplatesPage,
    roleCheck: celticsRights,
  },
  {
    path: '/policy-attributes/:templateId',
    component: RFQProductTemplatePages.RFQPolicyAttributesPage,
    roleCheck: celticsRights,
  },
  {
    path: '/configure-attribute/:templateId',
    component: RFQProductTemplatePages.RFQConfigureAttributePage,
    roleCheck: celticsRights,
  },
  {
    path: '/generate-pdf/:templateId',
    component: RFQProductTemplatePages.RFQGeneratePdfPage,
    roleCheck: celticsRights,
  },
  {
    path: '/save-product-template/:templateId',
    component: RFQProductTemplatePages.RFQSaveProductTemplatePage,
    roleCheck: celticsRights,
  },
  {
    path: '/plan-attributes/:templateId',
    component: RFQProductTemplatePages.RFQPlanAttributesPage,
    roleCheck: celticsRights,
  },
  {
    path: '/benefit-summary/:templateId',
    component: RFQProductTemplatePages.RFQBenefitSummaryPage,
    roleCheck: celticsRights,
  },
];

/**
 * @deprecated Use routesConfigV2 for new flows.
 */
const initiateQuoteRoutes: RouteConfig[] = [
  { path: '/initiate-rfq/:periodId?', component: InitiateRFQPages.InitiateRFQPage, roleCheck: brokerOrEmployerRights },
  {
    path: '/initiate-shelf/:periodId?',
    component: InitiateRFQPages.InitiateShelfPage,
    roleCheck: brokerOrEmployerRights,
  },
  {
    path: '/initiate-level-funded/:periodId?',
    component: InitiateRFQPages.InitiateLevelFundedPage,
    roleCheck: brokerOrEmployerRights,
  },
  { path: '/initiate-add-employees/:periodId', component: InitiateAddEmployeesPage, roleCheck: brokerOrEmployerRights },
  {
    path: '/census-initiate-add-employees-upload/:groupId',
    component: CensusInitiateAddEeUploadPage,
    roleCheck: enrollmentRights,
  },
  {
    path: '/census-initiate-add-employees-review/:groupId',
    component: CensusInitiateAddEeReviewPage,
    roleCheck: enrollmentRights,
  },
  {
    path: '/initiate-rfq-documents/:periodId',
    component: InitiateRFQPages.InitiateRFQDocumentsPage,
    roleCheck: brokerOrEmployerRights,
  },
  {
    path: '/submit-initiate-rfq/:periodId',
    component: InitiateRFQPages.SubmitRFQPage,
    roleCheck: brokerOrEmployerRights,
  },
  {
    path: '/pick-carriers/:periodId',
    component: InitiateRFQPages.PickCarriersRFQPage,
    roleCheck: brokerOrEmployerRights,
  },
  {
    path: '/download-imqs/:periodId',
    component: DownloadIMQsPage,
    roleCheck: brokerOrEmployerRights,
  },
  {
    path: '/browsing-review-carriers/:periodId',
    component: InitiateRFQPages.BrowsingReviewCarriersPage,
    roleCheck: brokerOrEmployerRights,
  },
];

/**
 * @deprecated Use routesConfigV2 for new flows.
 */
const responseQuoteRoutes: RouteConfig[] = [
  {
    path: '/broker-message/:periodId',
    component: RFQResponsePages.BrokerMessagePage,
    roleCheck: brokerOrEmployerRights,
  },
  { path: '/monitor-add-employees/:periodId', component: MonitorAddEmployeesPage, roleCheck: brokerOrEmployerRights },
  {
    path: '/census-monitor-add-employees-upload/:groupId',
    component: CensusMonitorAddEeUploadPage,
    roleCheck: enrollmentRights,
  },
  {
    path: '/census-monitor-add-employees-review/:groupId',
    component: CensusMonitorAddEeReviewPage,
    roleCheck: enrollmentRights,
  },
  {
    path: '/rfq-response-documents/:periodId',
    component: RFQResponsePages.ResponseRFQDocumentsPage,
    roleCheck: brokerOrEmployerRights,
  },
  { path: '/rfq-details/:periodId', component: RFQResponsePages.RFQDetailsPage, roleCheck: brokerOrEmployerRights },
  {
    path: '/response-proposal/:periodId',
    component: RFQResponsePages.RFQProposalResponsePage,
    roleCheck: brokerOrEmployerRights,
  },
  // Cover pages: CREATE_PORTFOLIO, RATE_RFQ, ADD_COLLATERAL, RELEASE
  { path: '/rfq-proposal/:periodId', component: RFQResponsePages.RFQProposalPage, roleCheck: celticsRights },
];

/**
 * @deprecated Use routesConfigV2 for new flows.
 */
const customQuoteRoutes: RouteConfig[] = [...initiateQuoteRoutes, ...responseQuoteRoutes];

export const routes = [
  ...generalRoutes,
  ...toolsRoutes,
  ...baToolsRoutes,
  ...subscriberRoutes,
  ...rfqTemplateRoutes,
  ...checklistsRoutes,
  ...customQuoteRoutes,
  ...utilityRoutes,
];
